import React, { useState, useEffect, useCallback } from "react";
import { withRouter } from "react-router-dom";
import { useSelector } from "react-redux";
import PdpParentPage from "../../react-components/pdp-parent-page";
import LoadingSpinner from "../../elements/loading-spinner";
import { toLowerCaseHyphenated } from "../../utils/siteUtils";
import api from "../../utils/api";
import { MathUtils } from "../../utils";
import { orderDetailsStatus, orderTypes, PdpSpuType, planType, financingTab } from "../../utils/lynkco-constants";
import useQuery from "../../utils/useQuery";
import { selectUserState } from "../../selectors/application";
import { appendQuery } from "../../utils/urlHelper";
import { canUseDOM } from "../../utils";
import "./style.scss";

const Pdp = props => {
  const { fields, lang, match, history, page } = props;
  const query = useQuery();
  const user = useSelector(selectUserState);

  const {
    car3d,
    buttonCTA,
    chatWithUsCTA,
    colorHeadingBody,
    colorHeadingType,
    colorIconModel,
    colorOptionsdescribes,
    colorAutoScroll,
    createAccount,
    createAccountBoxCTA,
    createAccountBoxHeadingBody,
    createAccountBoxHeadingType,
    createAccountBoxbody,
    disclaimerMtM,
    disclaimerBaC,
    engineHeadingBody,
    engineHeadingType,
    engineIconModel,
    engineOptionsdescribes,
    engineYesSuffix,
    engineYesSuffixBaC,
    engineAutoScroll,
    financingHeadingBody,
    financingHeadingType,
    financingIconModel,
    financingItem,
    financingSubmitCTA,
    footnoteSymbol,
    headingCaption,
    headingPeriod,
    locationInfoCaption,
    locationInfoModel,
    locationInfobody,
    loginDetail,
    notifyMeInfoCTA,
    outOfStockInfoBody,
    outOfStockInfoHeading,
    planHeadingBody,
    planHeadingType,
    planIconModel,
    planItems,
    planAutoScroll,
    planSelectDefault,
    refinePlanHeadingBody,
    refinePlanHeadingType,
    refinePlanIconModel,
    refinePlanSubmitCTA,
    stockModel,
    submitCTA,
    towbarHeadingBody,
    towbarHeadingType,
    towbarIconModel,
    towbarYesSuffix,
    towbarYesSuffixBaC,
    towbarOptionsdescribes,
    towbarAutoScroll,
    verifyEmail,
    winterTyresHeadingBody,
    winterTyresHeadingType,
    winterTyresIconModel,
    winterTyresAutoScroll,
    winterTyresOptionsdescribes,
    winterTyresAddShiftHeadingBody,
    winterTyresAddShiftHeadingType,
    winterTyresAddShiftIconModel,
    winterTyresAddShiftYesSuffix,
    winterTyresAddShiftOptionsdescribes,
    winterTyresAddShiftAutoScroll,
    noServiceConfirmationHeadingBody,
    noServiceConfirmationHeadingType,
    servicePackageButtonCTA,
    servicePackageDisclaimer,
    servicePackageHeadingBody,
    servicePackageHeadingType,
    servicePackageSureOptTextNext,
    servicePackageSureCheckboxLabel,
    servicePackageSureCheckboxLabelIsRequired,
    servicePackageIconModel,
    servicePackageOptionsdescribes,
    loanBanner,
    hideLoan,
    notWhiteListedRedirect
  } = fields;

  let { colors = [] } = car3d?.fields;
  colors = colors.map(color => ({
    key: color.fields.key.value,
    color: Number(color.fields.color.value),
    metalness: Number(color.fields.metalness.value)
  }));

  const [isLoadingBundle, setIsLoadingBundle] = useState(false);
  const [planButtons, setPlanButtons] = useState([]);
  const [planOptions, setPlanOptions] = useState({}); // Plan options are mapped to bundle ID
  const [servicePackageOptions, setServicePackageOptions] = useState([]);
  const [bundles, setBundles] = useState({});
  const [currentBundleId, setCurrentBundleId] = useState(null);
  const [productDataTable, setProductDataTable] = useState({});
  const [emptyPageHeight, setEmptyPageHeight] = useState("100%");

  useEffect(() => {
    async function init() {
      // only check if whitelisted if login is required on the page
      // if login is not required then allow all users to access.
      if (page?.fields?.loginRequired?.value && user?.customerNumber) {
        const { data } = await api.customer.cep.whitelist(null, {
          customerNumber: user?.customerNumber
        });
        const result = JSON.parse(data);
        const isWhiteListed = result.whiteListed === "Allowed";

        if (isWhiteListed) {
          getPlanButtons();
        } else {
          // redirect to sorry it is not you time yet page.
          history.replace(notWhiteListedRedirect?.value?.href);
        }
      } else {
        getPlanButtons();
      }
    }
    caclulateHeight();
    init();
  }, []);

  useEffect(() => {
    Promise.all(
      Object.values(bundles).map(async bundle => {
        const bundleId = bundle.bundleId;
        if (!(bundleId in planOptions)) {
          const data = {};
          const planId = planButtons.find(button => button.bundleId === bundleId).id;
          const { options, servicePackageOptions } = await getPlanOptions(bundle, planId, data);

          return { options: options, servicePackageOptions: servicePackageOptions, data: data, bundleId: bundleId };
        }
      })
    ).then(results => {
      const dataTable = {};
      const options = {};
      const servicePackageOptions = {};
      results.forEach(item => {
        dataTable[item.bundleId] = item.data;
        options[item.bundleId] = item.options;
        servicePackageOptions[item.bundleId] = item.servicePackageOptions;
      });
      getPresaleFinalizeOrder(options);
      setPlanOptions(options);
      setProductDataTable(dataTable);
      setServicePackageOptions(servicePackageOptions);
    });
  }, [bundles]);

  const handleResize = useCallback(() => {
    caclulateHeight();
  }, []);

  // Add event listener for resize
  useEffect(() => {
    global.addEventListener("resize", handleResize);
    return () => {
      global.removeEventListener("resize", handleResize, false);
    };
  }, [handleResize]);

  const caclulateHeight = () => {
    if (canUseDOM() && planButtons.length === 0) {
      const footerHeight = document?.getElementsByClassName("footer")[0]?.clientHeight;
      setEmptyPageHeight(`${window.innerHeight - (60 + footerHeight)}px`);
    }
  };

  const defaultHeadingLarge = { fields: { phrase: { value: "h3" } } };
  const defaultHeadingSmall = { fields: { phrase: { value: "h4" } } };

  /*  ------LEAVE THIS-----
    The data table contains information about products and their variants.

    "buy-a-car" : {
      spuId: 132,
      lowestMonthlyPrice: 0
      lowestPrice: 400000
      variants: {
        ["color-blue", engine-hev]: { skuId: 1890, price: 400000, monthlyPrice: 0, extNo: 1},
        ["color-black, engine-hev"]: { skuId: 1891, price: 400000, monthlyPrice: 0, extNo: 2}
        ["color-blue, engine-phev"]: { skuId: 1870, price: 500000, monthlyPrice: 0, extNo: 3}
        ["color-black, engine-phev"]: { skuId: 1871, price: 500000, monthlyPrice: 0, extNo: 4}
      }
    },
    "yes-towbar" : {
      spuId: 133,
      lowestMonthlyPrice: 0,
      lowestPrice: 200
      variants: {
        "yes-towbar": {skuId: 1770, monthlyPrice: 0, price: 200}
      }
    },
    ...etc

    This data is then used to get the correct price and skuId for each combination of products.
    This allows generic IDs to be used for each button, without adding duplicates with complicated dependencies.
    The table is fed into the PdpParentPage component where it can be used as a lookup.
  */

  const setDefaultRoute = buttons => {
    let planSelectDefaults = buttons.filter(item => item?.id === planSelectDefault?.fields?.phrase?.value)[0];
    let { lang, sitecoreRoute } = match.params;
    if (!match.params.service_options && planSelectDefaults) {
      const relatedOrderNo = query && query.relatedOrderNo;
      let newUrl = `/${lang}/${sitecoreRoute}/--/${toLowerCaseHyphenated(planSelectDefaults?.id)}`;
      newUrl = relatedOrderNo
        ? appendQuery(newUrl, {
            relatedOrderNo: relatedOrderNo
          })
        : newUrl;
      history.push(newUrl);
    }
  };

  const getPlanButtons = async () => {
    const buttons = planItems.map(planItem => {
      const bundleId = parseInt(planItem?.fields.bundleId?.value);
      const isMonthly = planItem?.fields?.monthBoolean?.value;
      const optPrevText = planItem?.fields?.optPrevText?.value;
      const optNextText = planItem?.fields?.optNextText?.value;

      const button = {
        id: planItem?.fields?.plan?.fields?.phrase?.value,
        parentLabel: "plan",
        clearOther: true,
        planType: "",
        isMembership: true,
        carePlanShow: planItem?.fields.carePlanShow?.value,
        bundleId: bundleId,
        summary: planItem?.fields?.summary,
        uspHtml: planItem?.fields?.describe,
        optPrevText: optPrevText,
        optNextText: optNextText,
        monthBoolean: isMonthly
      };

      switch (button.id) {
        case "buy-a-car":
          button.planType = planType.BaC;
          break;
        case "month-to-month":
          button.planType = planType.MtM;
          break;
      }

      return button;
    });

    setDefaultRoute(buttons);
    // Next, load the bundles asynchronously
    setIsLoadingBundle(true);
    Promise.all(
      buttons.map(async button => {
        const bundleId = button.bundleId;

        const { obj } = await api.product.bundle.detail({ bundleId: bundleId });
        const bundle = obj;

        return bundle;
      })
    ).then(newBundles => {
      setIsLoadingBundle(false);

      // When the buttons are loaded we can get data that depends on them.
      const newButtons = [...buttons];
      newButtons.forEach(button => {
        const bundle = newBundles.find(bundle => bundle.bundleId === button.bundleId);
        const vehicleSpu = bundle.spuList.find(spuItem => spuItem?.tag === PdpSpuType.vehicle);
        const price = button.monthBoolean ? vehicleSpu?.lowestMonthlyPrice : vehicleSpu?.lowestPrice;

        button.buttonText = bundle?.bundleName;
        button.optText = `${button.optPrevText}<span class='price'>${MathUtils.priceFormat(price, lang)}</span>${button.optNextText}`;
      });

      setPlanButtons(newButtons);
      setBundles(newBundles); // This will trigger the effect hook to load plan options
    });
  };

  const getPlanOptions = async (bundle, planId, table) => {
    let options = [];
    let wheelsServiceOption = {
      id: "choose-service-storage",
      heading: {
        html: winterTyresAddShiftHeadingBody,
        type: winterTyresAddShiftHeadingType || defaultHeadingSmall
      },
      info: winterTyresAddShiftIconModel,
      buttons: [],
      autoScroll: winterTyresAddShiftAutoScroll?.value,
      isSubOption: true,
      arrIndex: 4
    };
    let wheelsServicePrerequisites = [];
    let servicePackageOptions = [];
    bundle.spuList.forEach(spu => {
      if (hasParentSpu(spu, bundle.spuList)) {
        if (spu.tag === PdpSpuType.wheelsService) {
          const serviceStorageButtons = getServiceStorageButtons(spu, table);
          if (serviceStorageButtons?.length) {
            wheelsServiceOption.buttons = serviceStorageButtons;
          }
        }
      } else {
        switch (spu.tag) {
          case PdpSpuType.vehicle:
            /* COLOR AND ENGINE */
            const { colorButtons, engineButtons } = getCarOptions(spu, table, planId);
            if (colorButtons?.length) {
              const colorOption = {
                id: "color",
                heading: {
                  html: colorHeadingBody,
                  type: colorHeadingType || defaultHeadingLarge
                },
                info: colorIconModel,
                buttons: colorButtons,
                autoScroll: colorAutoScroll?.value,
                arrIndex: 0
              };
              options.push(colorOption);
            }
            if (engineButtons?.length) {
              const engineOption = {
                id: "engine",
                heading: {
                  html: engineHeadingBody,
                  type: engineHeadingType || defaultHeadingLarge
                },
                info: engineIconModel,
                buttons: engineButtons,
                autoScroll: engineAutoScroll?.value,
                arrIndex: 1
              };
              options.push(engineOption);
            }
            break;
          case PdpSpuType.towBar:
            /* TOWBAR */
            const towbarButtons = getTowbarButtons(spu, table, planId);
            if (towbarButtons?.length) {
              const towbarOption = {
                id: "towbar",
                heading: {
                  html: towbarHeadingBody,
                  type: towbarHeadingType || defaultHeadingLarge
                },
                info: towbarIconModel,
                buttons: towbarButtons,
                autoScroll: towbarAutoScroll?.value,
                arrIndex: 2
              };
              options.push(towbarOption);
            }
            break;
          case PdpSpuType.winterWheels:
            /* WINTER TYRES */
            const winterTyresButtons = getWinterTyresButtons(spu, table, planId);
            if (winterTyresButtons?.length) {
              wheelsServicePrerequisites = winterTyresButtons.map(x => x.button);
              winterTyresButtons.forEach(item => {
                const winterTyresOption = {
                  id: "winter-tyres",
                  heading: {
                    html: winterTyresHeadingBody,
                    type: winterTyresHeadingType || defaultHeadingLarge
                  },
                  info: winterTyresIconModel,
                  buttons: item.button,
                  autoScroll: winterTyresAutoScroll?.value,
                  arrIndex: 3,
                  prerequisites: [item.prerequisitesId]
                };
                options.push(winterTyresOption);
              });
            }
            break;
          case PdpSpuType.delivery:
            table["delivery"] = {
              delivery: spu.skuList[0],
              variants: {}
            };
            break;
          case PdpSpuType.servicePackage:
            /* SERVICE PACKAGE */

            const { mileageOptionsButtons, servicePackageButtons } = getServicePackageButtons(spu, table);
            if (servicePackageButtons?.length) {
              const servicePackageOption = {
                id: "service-package",
                heading: {
                  html: servicePackageHeadingBody,
                  type: servicePackageHeadingType
                },
                info: servicePackageIconModel,
                buttons: servicePackageButtons
              };
              servicePackageOptions.unshift(servicePackageOption);
            }
            break;
          case PdpSpuType.checkout:
            const { checkboxes } = getSerVicePackageCheckout(spu, table);
            const noServiceConfirmationOption = {
              id: "no-service-checkboxes",
              heading: {
                html: noServiceConfirmationHeadingBody,
                type: noServiceConfirmationHeadingType
              },
              isSubOption: true,
              checkboxes: checkboxes,
              prerequisites: ["no-service-plan"]
            };
            servicePackageOptions.push(noServiceConfirmationOption);
            break;
          default:
            break;
        }
      }
    });

    if (wheelsServiceOption.buttons.length) {
      wheelsServicePrerequisites.forEach((item, index) => {
        item.forEach(bttonItem => {
          if (bttonItem.id != "no-winter-tyres") {
            let option = { ...wheelsServiceOption };
            option.id = `wheels-service-${index}`;
            option.prerequisites = [bttonItem.id];
            options.push(option);
          }
        });
      });
    }
    options.sort((a, b) => {
      return a?.arrIndex - b?.arrIndex;
    });

    return { options, servicePackageOptions };
  };

  const hasParentSpu = (item, spuList) => {
    return spuList.filter(spu => spu.spuId === item.parentSpuId).length;
  };

  const getWinterTyresButtons = (spu, table, planId) => {
    let winterTyreButtons = [];

    const variants = {};
    const isMonthly = planId === "month-to-month";
    if (spu.skuList) {
      spu.skuList.forEach(sku => {
        let itemIdArr = sku.skuAttributeVos.map(attr => `${attr.defaultLanguageLabel}${attr.defaultLanguageValue}`.toLowerCase());
        let itemId = "";
        const currentEngine = sku.skuAttributeVos.filter(attr => attr.defaultLanguageLabel.toLowerCase() === "engine")[0];
        const engineId = currentEngine ? `${currentEngine?.defaultLanguageLabel}-${currentEngine?.defaultLanguageValue}`.toLowerCase() : "";
        (itemIdArr || []).forEach(idItem => {
          itemId += `${idItem} `;
        });
        itemId = itemId.trim();
        variants[itemId] = {
          skuId: sku?.skuId,
          price: isMonthly ? 0 : sku?.price || 0,
          monthlyPrice: isMonthly ? sku?.monthlyPrice || 0 : 0
        };
        const product = {
          spuId: spu.spuId,
          lowestPrice: isMonthly ? 0 : spu.lowestPrice || 0,
          lowestMonthlyPrice: isMonthly ? spu.lowestMonthlyPrice || 0 : 0,
          variants: variants // In this case, the product and the variant is the same because of how the IDs work. Maybe we can solve this more elegantly with parentLabel later somehow.
        };
        table[itemId] = product;
        if (sku.skuAttributeVos) {
          sku.skuAttributeVos.forEach((attr, index) => {
            const displayPrice = isMonthly ? sku.monthlyPrice : sku.price;
            if (attr.defaultLanguageLabel.toLowerCase() !== "engine") {
              winterTyreButtons.push({
                prerequisitesId: engineId,
                id: itemId,
                buttonText: attr.value,
                usableStock: sku?.usableStock,
                optText: `<span class='price'>+ ${MathUtils.priceFormat(displayPrice, lang)}</span>` // winterTyresYesSuffix?
              });
            }
          });
        }
      });
    }
    let arrNew = [];
    winterTyreButtons.forEach(winterTyreItem => {
      let obj = arrNew.find(arrItem => arrItem.prerequisitesId === winterTyreItem.prerequisitesId);
      if (!obj) {
        arrNew.push({
          prerequisitesId: winterTyreItem.prerequisitesId,
          button: [winterTyreItem]
        });
      } else {
        obj.button.push(winterTyreItem);
      }
    });
    if (spu.options) {
      const sku = spu.skuList[0];
      spu.options.forEach(option => {
        let button = {
          id: option?.isDefault === "1" ? "winter-tyres" : "no-winter-tyres",
          buttonText: option.optionName,
          optText: "",
          parentLabel: "winter-tyres"
        };
        if (option?.isDefault === "0") {
          arrNew.forEach(item => item.button.push(button));
        }
      });
      arrNew.forEach(item => {
        item.button.map((button, index) => (button.uspHtml = winterTyresOptionsdescribes[index]?.fields?.description?.value));
      });

      const product = {
        spuId: spu.spuId,
        lowestPrice: spu.lowestPrice,
        lowestMonthlyPrice: spu.lowestMonthlyPrice,
        variants: {
          "winter-tyres": {
            skuId: sku.skuId,
            extNo: sku.extNo,
            price: isMonthly ? 0 : sku.price || 0,
            monthlyPrice: isMonthly ? sku.monthlyPrice || 0 : 0
          }
        }
      };
      table["winter-tyres"] = product;
    }
    winterTyreButtons = arrNew;
    return winterTyreButtons;
  };

  const getCarOptions = (spu, table, planId) => {
    const engineButtons = [];
    const colorButtons = [];

    const attributeList = spu.skuAttributeList;
    const variants = {};

    const models = spu.ecomerceSpuAttributeList?.filter(x => x.defaultLanguageLabel.toLowerCase() === "model");
    const isMonthly = planId === "month-to-month";

    if (spu.skuList?.length) {
      spu.skuList.forEach(sku => {
        // Store the price and ID data that we need for orders
        const configuration = sku.skuAttributeVos.map(attr => `${attr.defaultLanguageLabel}-${attr.defaultLanguageValue}`.toLowerCase());

        variants[configuration] = {
          skuId: sku.skuId,
          extNo: sku.extNo,
          price: isMonthly ? 0 : sku.price || 0,
          monthlyPrice: sku.monthlyPrice || 0
        };
      });
    }

    attributeList.forEach(attr => {
      const label = attr.defaultLanguageLabel?.toLowerCase();

      switch (label) {
        case "color":
          attr.spuSkuAttibuteValueList.forEach((item, index) => {
            const color = colors.find(c => c.key === item?.defaultLanguageValue?.toLowerCase());
            const button = {
              id: `color-${item?.defaultLanguageValue?.toLowerCase()}`,
              buttonText: item?.value,
              skuAttrLabel: attr?.label,
              skuAttrDefaultLabel: attr?.defaultLanguageLabel,
              skuAttrDefaultValue: item?.defaultLanguageValue,
              uspHtml: colorOptionsdescribes[index]?.fields?.description?.value,
              buttonCallback(scene, canvas) {
                scene.callbacks.setCarColor(canvas, color);
              }
            };
            colorButtons.push(button);
          });
          break;
        case "engine":
          attr.spuSkuAttibuteValueList.forEach((item, index) => {
            const engineId = `engine-${item?.defaultLanguageValue?.toLowerCase()}`;
            const variantId = Object.keys(variants).find(key => key.includes(engineId));
            const relativePrice = isMonthly ? variants[variantId]?.monthlyPrice - spu.lowestMonthlyPrice : variants[variantId]?.price - spu.lowestPrice;
            const suffix = isMonthly ? engineYesSuffix?.value : engineYesSuffixBaC?.value;
            const button = {
              id: engineId,
              buttonText: item?.value,
              skuAttrLabel: attr?.label,
              skuAttrDefaultLabel: attr?.defaultLanguageLabel,
              skuAttrDefaultValue: item?.defaultLanguageValue,
              optText: relativePrice ? `<span class='price'>+ ${MathUtils.priceFormat(relativePrice, lang)}</span>${suffix ? " " + suffix : ""}` : "",
              uspHtml: engineOptionsdescribes[index]?.fields?.description?.value,
              model: models.length ? models[0].value : null
            };
            engineButtons.push(button);
          });
          break;
      }
    });

    const product = {
      spuId: spu.spuId,
      lowestPrice: isMonthly ? 0 : spu.lowestPrice || 0,
      lowestMonthlyPrice: isMonthly ? spu.lowestMonthlyPrice || 0 : 0,
      variants: variants
    };

    table[planId] = product;
    return { colorButtons, engineButtons };
  };

  const getTowbarButtons = (spu, table, planId) => {
    const buttons = [];

    const sku = spu.skuList.length ? spu.skuList[0] : null;
    const isMonthly = planId === "month-to-month";

    const product = {
      spuId: spu.spuId,
      lowestPrice: isMonthly ? 0 : sku.lowestPrice || 0,
      lowestMonthlyPrice: isMonthly ? sku.lowestMonthlyPrice || 0 : 0,
      variants: {
        "yes-towbar": {
          skuId: sku?.skuId,
          price: isMonthly ? 0 : sku?.price || 0,
          monthlyPrice: isMonthly ? sku?.monthlyPrice || 0 : 0,
          extNo: sku?.extNo
        }
      }
    };
    table["yes-towbar"] = product;

    if (spu.options) {
      spu.options.forEach((option, index) => {
        const button = {
          id: index === 0 ? "yes-towbar" : "no-towbar",
          buttonText: option.optionName,
          monthBoolean: false,
          price: 0,
          uspHtml: towbarOptionsdescribes[index]?.fields?.description?.value
        };
        const displayPrice = isMonthly ? spu?.skuList[0]?.monthlyPrice : spu?.skuList[0].price;
        if (option?.isDefault === "1") {
          const suffix = isMonthly ? towbarYesSuffix?.value : towbarYesSuffixBaC?.value;
          button.optText = displayPrice ? `<span class='price'>+ ${MathUtils.priceFormat(displayPrice, lang)}</span>${suffix ? " " + suffix : ""}` : "";
          button.monthly = true;
          button.usableStock = spu?.skuList[0]?.usableStock;
          button.price = spu?.skuList[0]?.price;
        }

        buttons.push(button);
      });
    }

    return buttons;
  };

  const getServiceStorageButtons = (spu, table) => {
    const buttons = [];

    const sku = spu.skuList.length ? spu.skuList[0] : null;

    const product = {
      spuId: spu.spuId,
      lowestPrice: 0, //spu.lowestPrice || 0,
      lowestMonthlyPrice: spu.lowestMonthlyPrice || 0,
      variants: {
        storage: {
          skuId: sku?.skuId,
          price: 0, //sku?.price || 0,
          monthlyPrice: sku?.monthlyPrice || 0,
          extNo: sku?.extNo
        }
      }
    };
    table["storage"] = product;

    if (spu.options) {
      spu.options.forEach((option, index) => {
        const button = {
          id: option?.isDefault === "1" ? "storage" : "no-storage",
          buttonText: option.optionName,
          optText: "",
          parentLabel: "service-storage",
          uspHtml: winterTyresAddShiftOptionsdescribes[index]?.fields?.description?.value
        };

        if (option?.isDefault === "1") {
          const monthlyPrice = sku?.monthlyPrice;
          button.usableStock = spu?.skuList[0]?.usableStock;
          button.optText = monthlyPrice ? `<span class='price'>+ ${MathUtils.priceFormat(monthlyPrice, lang)}</span> ${winterTyresAddShiftYesSuffix?.value}` : "";
        }

        buttons.push(button);
      });
    }

    return buttons;
  };

  const getServicePackageButtons = (spu, table) => {
    const servicePackageButtons = [];
    const mileageOptionsButtons = [];

    const variants = {};

    if (spu.options) {
      const sku = spu.skuList[0];

      spu.options.forEach((option, index) => {
        const isYesOption = option?.isDefault === "1";
        servicePackageButtons.push({
          spuIsRequired: spu?.isRequired,
          id: isYesOption ? "service-plan" : "no-service-plan",
          buttonText: option.optionName,
          usableStock: isYesOption ? sku?.usableStock : 1,
          uspHtml: servicePackageOptionsdescribes[index]?.fields?.description?.value,
          optText: sku?.monthlyPrice && isYesOption ? `<span class='price'>+ ${MathUtils.priceFormat(sku?.monthlyPrice, lang)}</span>${servicePackageSureOptTextNext?.value}` : ""
        });
      });
    }

    if (spu.skuList) {
      if (spu.skuList.length > 1) {
        spu.skuList.forEach((sku, index) => {
          const buttonId = `mileage-opt-${index}`;

          variants[buttonId] = {
            skuId: sku?.skuId,
            price: 0, //sku?.price || 0,
            monthlyPrice: sku?.monthlyPrice || 0
          };
          mileageOptionsButtons.push({
            id: buttonId,
            buttonText: sku?.value,
            optText: sku?.monthlyPrice ? `<span class='price'>+ ${MathUtils.priceFormat(sku?.monthlyPrice, lang)}</span>${servicePackageSureOptTextNext?.value}` : "",
            label: sku?.lable,
            valueText: sku?.value
          });
        });
      } else {
        // If there is only one option, we don't show choices, but we still need a variant to store the skuId.
        const sku = spu.skuList[0];

        variants["service-plan"] = {
          skuId: sku?.skuId,
          price: 0, //sku?.price || 0,
          monthlyPrice: sku?.monthlyPrice || 0,
          extNo: sku?.extNo
        };
      }
    }

    const product = {
      spuId: spu.spuId,
      lowestPrice: 0, //spu.lowestPrice,
      lowestMonthlyPrice: spu.lowestMonthlyPrice,
      variants: variants
    };
    table["service-plan"] = product;
    return { mileageOptionsButtons, servicePackageButtons };
  };
  const getSerVicePackageCheckout = (spu, table) => {
    let variants = {};
    let configurationArr = [];
    let checkboxes = [
      {
        label: servicePackageSureCheckboxLabelIsRequired?.value,
        id: "option1",
        isRequired: true,
        isCheckbox: true
      }
    ];
    if (servicePackageSureCheckboxLabel?.value) {
      checkboxes.push({
        label: servicePackageSureCheckboxLabel?.value,
        id: "option2",
        isCheckbox: true
      });
    }
    if (spu.skuList?.length) {
      spu.skuList.forEach(sku => {
        // Store the price and ID data that we need for orders
        let configuration = sku.skuAttributeVos.map(attr => {
          let str = attr.defaultLanguageValue.toLowerCase() !== "no" ? `${attr.defaultLanguageLabel}`.toLowerCase() : null;
          return str;
        });
        configuration.unshift("no-service-plan");
        configuration = configuration.filter(item => item);
        variants[configuration] = {
          skuId: sku.skuId,
          extNo: sku.extNo,
          price: sku.price || 0,
          monthlyPrice: sku.monthlyPrice || 0
        };
        configurationArr.push(configuration);
      });
    }
    configurationArr.sort((a, b) => {
      return b?.length - a?.length;
    });
    let variantsNew = {};
    configurationArr.forEach(item => (variantsNew[item] = variants[item]));
    const product = {
      spuId: spu.spuId,
      lowestPrice: spu.lowestPrice,
      lowestMonthlyPrice: spu.lowestMonthlyPrice,
      variants: variantsNew
    };
    table["no-service-plan"] = product;
    return { checkboxes };
  };
  const getPresaleFinalizeOrder = async option => {
    if (!user) return null;
    try {
      const { obj } = await api.order.latest.specify(null, {
        subType: orderTypes?.presale,
        orderStatus: [orderDetailsStatus?.close]
      });
      if (obj?.isPresaleFinalized === "1") return null;
      let colorId = "";
      let engineId = "";
      let skuAttributeList = obj?.suborders[0].orderItems[0].skuAttributeList;
      if (skuAttributeList?.length) {
        skuAttributeList.forEach(item => {
          let defaultLanguageLabel = item.defaultLanguageLabel?.toLowerCase();
          let defaultLanguageValue = item.defaultLanguageValue;
          switch (defaultLanguageLabel) {
            case "color":
              colorId = `/${defaultLanguageLabel}-${defaultLanguageValue}`;
              break;
            case "engine":
              engineId = `/${defaultLanguageLabel}-${defaultLanguageValue}`;
              break;
            default:
              break;
          }
        });
      }
      let { lang, sitecoreRoute } = match.params;
      let planSelect = planButtons.filter(item => item?.id === "buy-a-car")[0];
      let newUrl = "";
      if ((!match.params.service_options || planSelect?.id === match.params.service_options) && planSelect) {
        newUrl = `/${lang}/${sitecoreRoute}/--/${toLowerCaseHyphenated(planSelect?.id)}${toLowerCaseHyphenated(colorId)}${toLowerCaseHyphenated(engineId)}`;
      }
      const relatedOrderNo = obj?.orderNo || query.relatedOrderNo;
      let nextUrlNew = newUrl && relatedOrderNo ? appendQuery(newUrl, { relatedOrderNo: relatedOrderNo }) : newUrl;
      if (nextUrlNew) {
        history.push(nextUrlNew);
      }
    } catch (err) {
      console.dir(err);
    }
  };
  const locationInfo = {
    body: locationInfobody.value,
    button: {
      ...buttonCTA?.fields,
      buttonText: buttonCTA?.fields?.buttonText?.value
    },
    caption: locationInfoCaption?.value,
    modal: locationInfoModel?.fields
  };

  const pdpPriceProps = {
    price: "0",
    currency: "",
    period: headingPeriod?.value,
    size: "small",
    caption: headingCaption?.value,
    footnote: footnoteSymbol?.value
  };

  const defaultPlanOptions = bundles.length && Object.keys(planOptions).length ? planOptions[bundles[0].bundleId] : [];
  const defaultServicePackageOptions = servicePackageOptions.length && Object.keys(servicePackageOptions).length ? servicePackageOptions[bundles[0].bundleId] : [];

  const pdpPageProps = {
    optionList: {
      options: [
        {
          id: "choose-plan",
          heading: {
            html: planHeadingBody,
            type: planHeadingType || defaultHeadingLarge
          },
          info: planIconModel,
          buttons: planButtons,
          autoScroll: planAutoScroll?.value
        },
        ...(currentBundleId in planOptions ? planOptions[currentBundleId] : defaultPlanOptions)
      ],
      locationInfo: locationInfo,
      outOfStockInfo: {
        heading: outOfStockInfoHeading,
        body: outOfStockInfoBody?.value
      },
      disclaimerMtM: disclaimerMtM?.value,
      disclaimerBaC: disclaimerBaC?.value,
      chatButton: chatWithUsCTA?.fields,
      notifyMeInfo: {
        button: {
          ...notifyMeInfoCTA?.fields
        },
        modal: {
          ...stockModel?.fields,
          notifyError: false,
          notifySuccess: false
        }
      },
      acceptButton: { ...submitCTA?.fields, isDisabled: true }
    }
  };

  const setFinancingTab = () => {
    if (hideLoan?.value) {
      return {
        defaultOption: { id: financingTab.CASH, value: cashLabel.value, description: "" },
        options: [{ id: financingTab.CASH, value: cashLabel.value, description: "" }]
      };
    }
    return {
      defaultOption: { id: isDefaultLoan.value ? financingTab.LOAN : financingTab.CASH, value: isDefaultLoan.value ? loanLabel.value : cashLabel.value, description: "" },
      options: [
        { id: financingTab.CASH, value: cashLabel.value, description: "" },
        { id: financingTab.LOAN, value: loanLabel.value, description: "" }
      ]
    };
  };
  const refinePlanProps = {
    heading: {
      html: refinePlanHeadingBody,
      type: refinePlanHeadingType || defaultHeadingLarge
    },
    info: refinePlanIconModel,
    sliders: [],
    loanDisclaimer: "",
    cashDisclaimer: "",
    cashDescription: "",
    winterWheelsCheckbox: { label: "" },
    towbarCheckbox: { label: "" },
    checkoutButton: refinePlanSubmitCTA?.fields,
    onCheckout: plan => {}
  };
  const servicePackageProps = {
    options: [...(currentBundleId in servicePackageOptions ? servicePackageOptions[currentBundleId] : defaultServicePackageOptions)],
    disclaimer: servicePackageDisclaimer?.value,
    acceptButton: servicePackageButtonCTA?.fields
  };
  const { cashDescription, cashDisclaimer, loanSummary, cashSummary, loanDisclaimer, cashLabel, loanLabel, isDefaultLoan, downpaymentLabel, durationLabel, finalpaymentLabel, loanProductLabel, orderSummaryLabel, mileageLabel } = financingItem?.fields;
  const financingProps = {
    heading: {
      html: financingHeadingBody,
      type: financingHeadingType || defaultHeadingLarge
    },
    loanSummary,
    cashSummary,
    info: financingIconModel,
    tabs: setFinancingTab(),
    labels: {
      downpaymentLabel: downpaymentLabel?.value,
      durationLabel: durationLabel?.value,
      finalpaymentLabel: finalpaymentLabel?.value,
      loanProductLabel: loanProductLabel?.value,
      mileageLabel: mileageLabel?.value
    },
    sliders: [],
    cashOptions: {
      description: cashDescription,
      disclaimer: cashDisclaimer
    },
    loanDisclaimer: loanDisclaimer,
    checkoutButton: financingSubmitCTA?.fields,
    orderSummaryLabel: orderSummaryLabel,
    onCheckout: plan => {}
  };
  const buyCarSummary = {
    heading: {
      html: "",
      type: ""
    },
    features: [],
    info: "",
    includedFeatures: {
      heading: {
        html: "",
        type: ""
      },
      body: ``
    }
  };
  const accountPreviewProps = {
    lang,
    heading: {
      html: loginDetail?.fields?.formTitleBody,
      type: loginDetail?.fields?.formTitleType
    },
    body: loginDetail?.fields?.description?.value,

    loginForm: {
      ...loginDetail?.fields
    },
    createAccountBox: {
      createAccountBoxHeadingType,
      createAccountBoxHeadingBody,
      createAccountBoxbody,
      createAccount: {
        ...createAccountBoxCTA?.fields,
        buttonText: createAccountBoxCTA?.fields?.buttonText?.value,
        isDisabled: false
      }
    },
    createAccount: {
      isAccountPreview: true,
      onAccountCreate: (evt, data) => console.log("onAccountCreate triggered", data),
      onVerificationCodeSubmited: (evt, data) => console.log("onVerificationCodeSubmited triggered", data),
      onResendVerificationCode: (evt, data) => console.log("onResendVerificationCode triggered", data),

      collectInfoStep: {
        createAccountForm: {
          ...createAccount?.fields,
          createAccountFailed: false
        }
      },
      verifyStep: {
        verifyEmailForm: {
          ...verifyEmail?.fields
        }
      }
    },
    submitLogin: (evt, data) => {}
  };

  const { defaultEngine, defaultColorIndex, defaultTowbar, defaultSnow } = car3d.fields;

  const sceneData = {
    colors: {},
    defaults: {
      color: colors[parseInt(defaultColorIndex?.value)] || colors[0],
      engine: defaultEngine?.value || "engine-phev",
      towbar: defaultTowbar?.value,
      snow: defaultSnow.value
    }
  };

  colors.forEach(color => {
    sceneData.colors[color.key] = color;
    if (match?.url && match?.url.includes(`color-${color.key}`)) {
      sceneData.defaults.color = color;
    }
  });

  // This is all a bit of a hack... sorry about that. But we need the defaults before ModelViewer mounts and
  // the scene initializes, so we can't use the regular routing logic.

  if (match?.url) {
    if (match.url.includes("engine-hev")) {
      sceneData.defaults.engine = "engine-hev";
    }
    if (match.url.includes("no-winter-tyres")) {
      sceneData.defaults.snow = false;
    } else {
      if (match.url.includes("winter")) {
        sceneData.defaults.snow = true;
      }
    }
    if (match.url.includes("yes-towbar")) {
      sceneData.defaults.towbar = true;
    }
  }

  const productData = currentBundleId && productDataTable ? productDataTable[currentBundleId] : {};
  if (planButtons.length > 0) {
    return (
      <PdpParentPage
        match={match}
        loanBanner={loanBanner}
        productDataTable={productData}
        bundleId={currentBundleId}
        fields={fields}
        accountPreview={accountPreviewProps}
        pdpPageProps={pdpPageProps}
        pdpPriceProps={pdpPriceProps}
        servicePackageProps={servicePackageProps}
        refinePlanProps={refinePlanProps}
        financingProps={financingProps}
        buyCarSummary={buyCarSummary}
        sceneData={sceneData}
        isFetchingNewData={isLoadingBundle}
        onPlanChanged={newPlan => {
          newPlan?.bundleId && setCurrentBundleId(newPlan.bundleId);
        }}
      />
    );
  } else {
    return (
      <div className="pdp-full-page-spinner" style={{ height: emptyPageHeight }}>
        <LoadingSpinner />
      </div>
    );
  }
};

export default withRouter(Pdp);
